/**
 * Section property-panel controls for decorative SVGs.
 * Uses inline SVG storage so inserted decoratives are preview/export safe.
 */

import { editableIdRepairAllocationReady } from '../editing/editable-id-repair-admission.js';
import { store } from '../core/state.js';
import { eventBus } from '../core/events.js';
import { resolveSectionByInstance } from '../editing/section-utils.js';
import { openSvgLibraryForInsert } from '../editing/image-editor.js';
import { openReplaceDialog } from '../editing/replace-dispatcher.js';
import { showUndoToast } from './shell.js';
import {
  insertDecorativeSvg,
  listDecorativeSvgs,
  removeDecorativeSvg,
  setDecorativeSvgColor,
  canRestoreDecorativeOriginal,
  serializeDecorativePreview
} from '../editing/insert-decorative-svg.js';
import { reportError } from '../core/report-error.js';
import { escapeHTML as escapeHtml } from '../shared/html-escape.js';
import { computeSectionOverrides } from './section-properties/apply-section-override.js';
import { applyDecorativeMutation } from '../editing/decorative-mutations.js';
import { buildDecorativeReplaceSlots } from '../editing/decorative-slot-queue.js';
import { formatSectionName } from '../shared/section-display-name.js';
import { domTargetRef } from '../shared/target-ref.js';

/**
 * "Adding a shape to Hero" — the sentence `createModal({ context })` renders
 * under the dialog's title.
 *
 * Returns the lead alone when the section cannot be resolved, rather than an
 * empty wrapper: a context line reading "… to" is worse than none.
 */
function decorativeTargetContext(instanceId, lead) {
  const resolved = resolveSectionByInstance(instanceId);
  const name = resolved ? formatSectionName(resolved.section) : '';
  return name && name !== 'Section' ? `${lead} in ${name}` : lead;
}

// DS-6: this WAS a hand-written copy of the same three steps the + gap handler
// in shell.js also carried. One home now — see decorative-mutations.js for why
// the drift it prevents is not hypothetical.
const applySectionMutation = applyDecorativeMutation;

function buildPreview(item) {
  const thumb = document.createElement('div');
  thumb.className = 'pb-decorative-thumb pb-checker';

  if (item.tagName === 'img' && item.src) {
    const img = document.createElement('img');
    img.src = item.src;
    img.alt = '';
    img.loading = 'lazy';
    thumb.appendChild(img);
    return thumb;
  }

  // DS-2: the row previews the shape's ACTUAL token, not a fixed one. The
  // markup is already transformed (it came out of the section), so passing its
  // own token is a no-op on paint — what it buys is the inline `color`, which
  // is what makes a `currentColor` shape render as the project's colour here
  // instead of the builder's text colour.
  const previewMarkup = serializeDecorativePreview(item.markup, {
    colorToken: item.colorToken || 'original'
  });
  if (previewMarkup) {
    thumb.innerHTML = previewMarkup;
  } else {
    thumb.textContent = 'SVG';
  }
  return thumb;
}

function renderList(listEl, items, instanceId, rerender) {
  listEl.innerHTML = '';
  if (items.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'pb-decorative-empty';
    empty.textContent = 'No decorative SVGs in this section';
    listEl.appendChild(empty);
    return;
  }

  for (const item of items) {
    const row = document.createElement('div');
    const canSelectSvg = item.tagName === 'svg' && item.eid;
    row.className = `pb-decorative-row${canSelectSvg ? ' pb-decorative-row-selectable' : ''}`;
    row.dataset.decorativeIndex = String(item.index);
    if (canSelectSvg) {
      row.tabIndex = 0;
      row.setAttribute('role', 'button');
      row.setAttribute('aria-label', `Edit ${item.name}`);
    }

    const preview = buildPreview(item);
    row.appendChild(preview);

    const meta = document.createElement('div');
    meta.className = 'pb-decorative-meta';
    meta.innerHTML = `
      <span class="pb-decorative-name">${escapeHtml(item.name)}</span>
      <span class="pb-decorative-kind">${escapeHtml(item.tagName.toUpperCase())}</span>
    `;
    row.appendChild(meta);

    const actions = document.createElement('div');
    actions.className = `pb-decorative-actions${item.tagName === 'svg' ? ' pb-decorative-actions--has-color' : ''}`;

    if (item.tagName === 'svg') {
      const colorSelect = document.createElement('select');
      colorSelect.className = 'pb-decorative-color-select';
      colorSelect.setAttribute('aria-label', `Color for ${item.name}`);
      // DS-2: "Original" is FIRST and it is the default. Plenty of these shapes
      // are coloured the way they are for the stitch they came from, and the
      // insert used to destroy that unconditionally.
      //
      // It is OFFERED ONLY WHEN IT CAN BE DELIVERED. A decorative inserted
      // before DS-2 has `--primary` and no recorded paint — its original was
      // flattened and never written down — so for those the option is absent
      // and the row says why, rather than present and dead.
      const canRestore = canRestoreDecorativeOriginal(
        resolveSectionByInstance(instanceId)?.section?.html || '',
        item.index
      );
      const colorOptions = [
        ...(canRestore ? [['original', 'Original']] : []),
        ['--primary', 'Primary'],
        ['--secondary', 'Secondary'],
        ['--headerColor', 'Heading'],
        ['--bodyTextColor', 'Body'],
        ['currentColor', 'Inherit']
      ];
      const current = item.colorToken || 'original';
      for (const [value, label] of colorOptions) {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = label;
        option.selected = current === value;
        colorSelect.appendChild(option);
      }
      colorSelect.addEventListener('click', (event) => event.stopPropagation());
      colorSelect.addEventListener('change', (event) => {
        event.stopPropagation();
        const resolved = resolveSectionByInstance(instanceId);
        if (!resolved) return;
        const result = setDecorativeSvgColor(resolved.section.html, item.index, colorSelect.value);
        if (!result.ok) {
          // The one reason a user can act on. `not-found` / `not-svg` mean the
          // panel is out of date with the section, which a rerender fixes.
          if (result.reason === 'no-recorded-paint') {
            reportError('decorative-color', new Error('no-recorded-paint'), {
              user: 'toast',
              message:
                'This shape was added before PageStitch recorded original colours, so it cannot be put back. Replace it to get its own colours.'
            });
          }
          rerender();
          return;
        }
        store.update(resolved.path, result.html, 'Change decorative SVG color');
        eventBus.emit('preview:render-request', { preserveScroll: true });
        rerender();
      });
      actions.appendChild(colorSelect);
    }

    const replaceBtn = document.createElement('button');
    replaceBtn.type = 'button';
    replaceBtn.className = 'pb-props-btn pb-props-btn-small';
    replaceBtn.textContent = 'Replace';
    replaceBtn.addEventListener('click', (event) => {
      event.stopPropagation();
      const currentHtml = resolveSectionByInstance(instanceId)?.section?.html || '';
      const currentItems = listDecorativeSvgs(currentHtml);
      const targetFor = (candidate) => ({
        instanceId,
        editableType: candidate.editableType || 'svg',
        editableIndex: candidate.editableIndex,
        eid: candidate.eid,
        targetRef: domTargetRef(candidate.eid),
        tagName: candidate.tagName.toUpperCase(),
        kind: 'decoration'
      });
      const target = targetFor(item);
      const queueSlots = buildDecorativeReplaceSlots(currentHtml, {
        eid: item.eid,
        instanceId
      }).flatMap((slot) => {
        const candidate = currentItems.find((entry) => entry.eid === slot.data.eid);
        if (!candidate) return [];
        const slotTarget = targetFor(candidate);
        return [{ ...slot, ...slotTarget, data: { ...slot.data, target: slotTarget } }];
      });
      void openReplaceDialog({
        target,
        slots: queueSlots.length ? queueSlots : [target],
        context: decorativeTargetContext(instanceId, `Replacing ${item.name}`)
      }).then((outcome) => {
        if (outcome.status === 'picked') rerender();
      });
    });

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'pb-props-btn pb-props-btn-small pb-props-btn-danger';
    removeBtn.textContent = 'Remove';
    removeBtn.addEventListener('click', (event) => {
      event.stopPropagation();
      const resolved = resolveSectionByInstance(instanceId);
      if (!resolved) return;
      const updatedHtml = removeDecorativeSvg(resolved.section.html, item.index);
      if (!updatedHtml) return;
      store.update(resolved.path, updatedHtml, 'Remove decorative SVG');
      showUndoToast('Decorative SVG removed');
      eventBus.emit('preview:render-request', { preserveScroll: true });
      rerender();
    });

    actions.append(replaceBtn, removeBtn);
    row.appendChild(actions);
    if (canSelectSvg) {
      const selectSvg = () => {
        eventBus.emit('editing:svg-selected', {
          instanceId,
          editableType: 'svg',
          editableIndex: item.editableIndex >= 0 ? item.editableIndex : 0,
          eid: item.eid
        });
      };
      row.addEventListener('click', selectSvg);
      row.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        selectSvg();
      });
    }
    listEl.appendChild(row);
  }
}

// How far the shape hangs past the section's bottom edge, as a percentage of
// its own height. Off is first and is the default, so no existing project moves.
const BLEED_OPTIONS = [
  ['', 'Off (clipped)'],
  ['25%', 'Slight'],
  ['50%', 'Half'],
  ['75%', 'Most'],
  ['100%', 'Full']
];

/**
 * "Bleed into next section" — the escape hatch for the clip that
 * `insert-decorative-svg.js` stamps on every host section.
 *
 * Inserting a decorative SVG sets `overflow: hidden; isolation: isolate` on the
 * section and pins the shape to `bottom: 0`. A wave meant to flow over the
 * footer therefore stops dead at the section boundary, and until now nothing in
 * the UI could undo that.
 *
 * This is a SECTION-level override, not a per-SVG one, and deliberately so: the
 * property being changed (`overflow`) belongs to the host section, and the
 * decorative CSS in `section.css` is deduped by CATALOG KEY, so a per-instance
 * value written there would not even apply to the second instance of a repeated
 * section. Overrides are per-instance and win with `!important`.
 */
function renderBleedControl(panelEl, instanceId, items) {
  if (!panelEl || !items.length) return;
  const resolved = resolveSectionByInstance(instanceId);
  if (!resolved) return;

  const current = resolved.section?.overrides?.decorativeBleed || '';

  const field = document.createElement('div');
  field.className = 'pb-decorative-bleed';

  const label = document.createElement('label');
  label.className = 'pb-decorative-bleed-label';
  label.textContent = 'Bleed into next section';

  const select = document.createElement('select');
  select.className = 'pb-decorative-color-select';
  select.setAttribute(
    'title',
    'Let the shape hang past the bottom of this section and paint over the one below.'
  );
  for (const [value, text] of BLEED_OPTIONS) {
    const option = document.createElement('option');
    option.value = value;
    option.textContent = text;
    option.selected = current === value;
    select.appendChild(option);
  }
  const selectId = `pb-decorative-bleed-${String(instanceId).replace(/[^a-zA-Z0-9_-]/g, '')}`;
  select.id = selectId;
  label.setAttribute('for', selectId);

  select.addEventListener('click', (event) => event.stopPropagation());
  select.addEventListener('change', (event) => {
    event.stopPropagation();
    const latest = resolveSectionByInstance(instanceId);
    if (!latest) return;
    // computeSectionOverrides is the one shape engine for overrides (16B2);
    // resolveSectionByInstance is used instead of applySectionOverride because
    // that helper resolves PAGE sections only, and a decorative SVG can sit on
    // a shared nav or footer.
    const { overrides, changedFields } = computeSectionOverrides(latest.section, {
      decorativeBleed: select.value
    });
    if (!changedFields.length) return;
    store.update(`${latest.sectionPath}.overrides`, overrides, 'Change decorative bleed');
    eventBus.emit('preview:render-request', { preserveScroll: true });
  });

  field.append(label, select);
  panelEl.appendChild(field);
}

/**
 * Trigger the SVG-library picker and insert the chosen SVG into the section.
 *
 * Extracted so both the legacy inline panel header and the Phase 7 drawer
 * header (`svg-drawer.js`) wire their Insert buttons to the same flow —
 * single source of truth for the insert mutation + emission contract.
 *
 * @param {string} instanceId
 * @param {HTMLElement} triggerEl - anchor for the SVG library popover
 * @param {() => void} [onInserted] - called after a successful insert so
 *   the host UI can rerender its list / count.
 */
export function triggerInsertDecorativeSvg(instanceId, triggerEl, onInserted, opts = {}) {
  openSvgLibraryForInsert({
    triggerEl,
    context: opts.context || decorativeTargetContext(instanceId, 'Adding a shape'),
    onPick: (svgMarkup, indexEntry) => {
      const latest = resolveSectionByInstance(instanceId);
      if (!latest) return;
      if (!editableIdRepairAllocationReady(latest.section, store.getState())) return;
      const result = insertDecorativeSvg(latest.section, svgMarkup, indexEntry, {
        edge: opts.edge
      });
      if (!result.updatedHtml) return;
      applySectionMutation(
        latest,
        result.updatedHtml,
        result.addedScopedCss,
        'Insert decorative SVG'
      );
      // D8 / the PP-0 rule: element add ends with an undo- toast, and only
      // REMOVAL had one. THE UNDO IS THE CONFIRMATION — never a success toast
      // beside it. Under a queue this fires per pick, which is the honest shape
      // (G8): each pick applies immediately so the canvas keeps up, so the run
      // is N undoable entries and N toasts, not one.
      showUndoToast('Decorative SVG added');
      if (typeof onInserted === 'function') onInserted();
    }
  });
}

/**
 * Render the decorative-SVG panel inside `container`.
 *
 * @param {HTMLElement} container
 * @param {string} instanceId
 * @param {{
 *   headerless?: boolean,
 *   onCountChange?: (count: number, countLabel: string) => void
 * }} [opts]
 *   - `headerless` (Phase 7 drawer): when true, the inline title + count +
 *     Insert button are NOT rendered. The drawer hosts those in its own
 *     title bar instead. The body becomes a clean list.
 *   - `onCountChange`: fires on every render with the current count so the
 *     drawer wrapper can keep its title in sync with insert / remove edits.
 *
 * @returns {{ rerender: () => void }}
 *   `rerender` re-renders the panel with the same options. The drawer
 *   wrapper holds onto this and uses it as the `onInserted` callback for
 *   its drawer-header Insert button.
 */
export function renderDecorativeSvgPanel(container, instanceId, opts = {}) {
  const noop = () => {};
  if (!container) return { rerender: noop };
  const resolved = resolveSectionByInstance(instanceId);
  if (!resolved) return { rerender: noop };

  const headerless = !!opts.headerless;
  const onCountChange = typeof opts.onCountChange === 'function' ? opts.onCountChange : null;

  const items = listDecorativeSvgs(resolved.section.html);
  const countLabel = items.length === 1 ? '1 asset' : `${items.length} assets`;

  if (headerless) {
    container.innerHTML = `
      <div class="pb-props-section pb-decorative-panel pb-decorative-panel-headerless">
        <div class="pb-decorative-list"></div>
      </div>
    `;
  } else {
    container.innerHTML = `
      <div class="pb-props-section pb-decorative-panel">
        <div class="pb-decorative-header">
          <div>
            <div class="pb-props-section-title">Decorative SVG</div>
            <div class="pb-decorative-count">${countLabel}</div>
          </div>
          <button type="button" class="pb-props-btn pb-decorative-insert">Insert</button>
        </div>
        <div class="pb-decorative-list"></div>
      </div>
    `;
  }

  const rerender = () => renderDecorativeSvgPanel(container, instanceId, opts);
  const listEl = container.querySelector('.pb-decorative-list');
  renderList(listEl, items, instanceId, rerender);
  renderBleedControl(container.querySelector('.pb-decorative-panel'), instanceId, items);

  if (!headerless) {
    container.querySelector('.pb-decorative-insert').addEventListener('click', (event) => {
      triggerInsertDecorativeSvg(instanceId, event.currentTarget, rerender);
    });
  }

  if (onCountChange) {
    try {
      onCountChange(items.length, countLabel);
    } catch (err) {
      console.error('[decorative-svg-panel] onCountChange threw:', err);
    }
  }

  return { rerender };
}
